import { selectedFeatureAtom } from '@/atoms';
import {
  addLineModeAtom,
  addPointModeAtom,
  addPolygonModeAtom,
  addSurfaceModeAtom,
} from '@/atoms/viewer.atom';
import { ProjectShareDialogContent } from '@/components';
import FullPageError from '@/components/FullPageError';
import { Button } from '@/components/ui/button';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { getLinkSessionId } from '@/graphql/execute';
import { Project } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton, { iAction } from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import { useDialog } from '@/providers/DialogProvider';
import { CogFourSolid, DangerTriangleSolid, Edit, LayersThree, SaveSolid, Share, Upload } from '@mynaui/icons-react';
import { useAtom, useAtomValue } from 'jotai';
import { BarChart2 } from 'lucide-react';
import { FunctionComponent, lazy, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
const Viewer = lazy(() => import('@/components/Viewer'));

const checkWebGLSupport = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      !!window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
};

interface iViewerProps {}

export const useViewerActions = (projectId: string, runningDeviationCount: number = 0) => {
  const navigate = useNavigate();
  const { organization, hasRole } = useOrganizations();
  const { openDialog } = useDialog();
  const { t } = useTranslation('project');
  const [params, setParams] = useSearchParams();
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const isMeasurementsEnabled = params.get('showMeasurements') === 'true';
  const isLayersEnabled = params.get('layers') === 'true';

  const _handleToggleLayers = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('layers', isLayersEnabled ? 'false' : 'true');
    analytics.capture(isLayersEnabled ? 'Project Layers Disabled' : 'Project Layers Enabled');
    setParams(params);
  };

  const _handleUploadData = () => {
    if (!organization) throw new Error('Organization is not defined');
    analytics.capture('Project Upload Opened');
    navigate(`/projects/${projectId}/upload`);
  };

  const _handleEditProject = () => {
    if (!organization) throw new Error('Organization is not defined');
    analytics.capture('Project Edit Opened');
    navigate(`/projects/${projectId}/edit`);
  };

  const _handleOpenAnalyses = () => {
    analytics.capture('Project Analyses Opened');
    navigate(`/projects/${projectId}/analyses`);
  };

  const _handleOpenSettings = () => {
    analytics.capture('Project Settings Opened');
    setSettingsDialogOpen(true);
  };

  const _handleOpenShare = () => {
    analytics.capture('Project Share Opened');
    openDialog(<ProjectShareDialogContent projectId={projectId!} />, 'sm:max-w-[800px]');
  };

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const mod = isMac ? '⌘' : 'Ctrl';

  const actions: (iAction | null)[] = [
    {
      icon: <LayersThree />,
      onClick: _handleToggleLayers,
      tooltip: t('viewer.toggleLayers'),
      appearance: isLayersEnabled ? 'primary' : 'secondary',
      shortcutKey: `${mod}+⇧+L`,
    },
    {
      icon: <CogFourSolid />,
      onClick: _handleOpenSettings,
      tooltip: t('viewer.viewerSettings'),
      appearance: 'secondary',
    },
  ];

  // Shared-link visitors must never see deviation analysis controls, even if
  // they happen to also have an EDITOR role — shared links bypass the normal
  // auth flow and the deviation pipeline runs expensive ECS jobs that we
  // don't want kicked off via shared sessions. Upload is already permitted
  // on shared links via the quick-upload allow-list, so it stays under the
  // normal hasRole('EDITOR') gate.
  const isSharedLink = !!getLinkSessionId();

  if (hasRole('EDITOR')) {
    const editorActions: (iAction | null)[] = [
      {
        icon: (
          <span id='onboarding-upload' className='flex size-full items-center justify-center'>
            <Upload />
          </span>
        ),
        onClick: _handleUploadData,
        tooltip: t('viewer.dataCenter'),
        shortcutKey: `${mod}+⇧+U`,
      },
    ];
    if (!isSharedLink) {
      editorActions.push({
        icon: (
          <div className='relative'>
            <BarChart2 />
            {runningDeviationCount > 0 && (
              <span className='absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-blue-500'>
                <span className='absolute h-3 w-3 animate-ping rounded-full bg-blue-400 opacity-75' />
              </span>
            )}
          </div>
        ),
        onClick: _handleOpenAnalyses,
        tooltip: t('viewer.openAnalyses'),
      });
    }
    editorActions.push(null);
    actions.unshift(...editorActions);
  }
  if (hasRole('EDITOR')) {
    actions.unshift({
      icon: (
        <span id='onboarding-edit' className='flex size-full items-center justify-center'>
          <Edit />
        </span>
      ),
      onClick: _handleEditProject,
      tooltip: t('viewer.editProject'),
      shortcutKey: `${mod}+⇧+E`,
    });
  }
  if (hasRole('EDITOR')) {
    actions.unshift({
      icon: (
        <span id='onboarding-share' className='flex size-full items-center justify-center'>
          <Share />
        </span>
      ),
      onClick: _handleOpenShare,
      tooltip: t('viewer.shareProject'),
    });
  }

  return { actions, isSettingsDialogOpen: settingsDialogOpen, setSettingsDialogOpen };
};

const ViewerPage: FunctionComponent<iViewerProps> = () => {
  const { projectId } = useParams();
  const { t } = useTranslation('project');
  const isWebGLSupported = useMemo(() => checkWebGLSupport(), []);

  const { project, projectLoading, deviationRunningCount } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    deviationRunningCount: number;
  }>();

  usePageMeta({ title: [project?.name] });

  const isSharedLink = !!getLinkSessionId();
  // The polling/toast hook is mounted in `ProjectWrapper` (the project shell)
  // so it stays alive across viewer ↔ analyses ↔ attribute-table navigation,
  // and so triggering Run-Deviation from the Layers panel of any sub-page
  // surfaces the toast and starts polling. We just consume its result here
  // for the badge. Hide the badge entirely on shared links.
  const runningCount = isSharedLink ? 0 : deviationRunningCount;
  const { actions, isSettingsDialogOpen, setSettingsDialogOpen } = useViewerActions(projectId!, runningCount);

  const href = `/projects/${projectId}`;

  if (!isWebGLSupported) {
    return (
      <div className='max-h-screen overflow-hidden flex flex-col flex-1'>
        <PageSkeleton
          actions={actions}
          steps={projectLoading || !project ? [{ label: t('edit.loading'), href }] : [{ label: project.name, href }]}>
          <div className='flex flex-col items-center justify-center flex-1'>
            <DangerTriangleSolid className='size-24 text-rose-500' />

            <span className='max-w-96 text-2xl font-semibold mt-12 text-center whitespace-normal'>
              {t('viewer.browserNotSupported')}
            </span>
            <span className='max-w-96 text-sm opacity-70 mt-4 text-center whitespace-normal'>
              {t('viewer.browserNotSupportedDescription')}
            </span>
            <span className='max-w-96 text-sm opacity-70 mt-2 text-center whitespace-normal'>
              {t('viewer.browserNotSupportedContact')}
            </span>
          </div>
        </PageSkeleton>
      </div>
    );
  }
  return (
    <div className='max-h-screen overflow-hidden flex flex-col flex-1'>
      <PageSkeleton
        actions={actions}
        steps={projectLoading || !project ? [{ label: t('edit.loading'), href }] : [{ label: project.name, href }]}>
        <div className='overflow-hidden flex flex-col flex-1 relative'>
          <ErrorBoundary
            key={projectId}
            fallbackRender={({ error }) => (
              <FullPageError
                title={t('viewer.failedRender3d')}
                subtitle={t('viewer.failedRender3dDescription')}
                error={error}
              />
            )}>
            <Viewer projectId={projectId!}>
              <FeatureMoveActions />
            </Viewer>
          </ErrorBoundary>
        </div>
      </PageSkeleton>
      {isSettingsDialogOpen && (
        <ViewerSettingsDialog open={isSettingsDialogOpen} onOpenChange={setSettingsDialogOpen} />
      )}
    </div>
  );
};

export const FeatureMoveActions: FunctionComponent = () => {
  const [selectedFeature, setSelectedFeature] = useAtom(selectedFeatureAtom);
  const addPointMode = useAtomValue(addPointModeAtom);
  const addLineMode = useAtomValue(addLineModeAtom);
  const addPolygonMode = useAtomValue(addPolygonModeAtom);
  const addSurfaceMode = useAtomValue(addSurfaceModeAtom);
  const { t } = useTranslation('project');
  const _handleCancel = () => {
    setSelectedFeature(null);
  };
  const _handleSave = () => {
    if (!selectedFeature) return;
    selectedFeature.onSave(selectedFeature);
  };

  // Suppressed while any creation tool is active — those tools own their own
  // top-middle FloatingToolbar with OK / Cancel, so a second floating
  // commit surface in the bottom-right is redundant and would let the user
  // save through a path that doesn't reset the tool's mode atom.
  const anyCreationToolActive =
    addPointMode.enabled ||
    addLineMode.enabled ||
    addPolygonMode.enabled ||
    addSurfaceMode.enabled;

  // Esc cancels the move. Listen at window-level so it works regardless of
  // canvas focus. Only active while a feature is being moved.
  useEffect(() => {
    if (!selectedFeature?.isMoving) return;
    if (anyCreationToolActive) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedFeature(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedFeature?.isMoving, anyCreationToolActive, setSelectedFeature]);

  if (!selectedFeature?.isMoving) return null;
  if (anyCreationToolActive) return null;
  return (
    <div className='absolute bottom-4 right-4 z-20 flex gap-2'>
      <Button variant='ghost' className='text-black bg-white/40 backdrop-blur-md' onClick={_handleCancel}>
        {t('viewer.cancel')}
      </Button>
      <Button variant='default' onClick={_handleSave}>
        <SaveSolid />
        {t('viewer.save')}
      </Button>
    </div>
  );
};

export default ViewerPage;
